// ============================================
// Demo Auth Service - Mock Only (No Backend)
// ============================================

// 데모 사용자 목록 (이메일 + 비밀번호로 매칭)
const DEMO_USERS = [
    {
        id: 999,
        email: 'demo@seoro.ai.kr',
        password: 'demo1234',
        name: '데모 사용자',
        role: 'ADMIN',
        schemaName: 'seoro',
        companyName: '(주)서로',
        permissions: ['*'] // 모든 권한
    },
    {
        id: 1000,
        email: 'rootechs@seoro.ai.kr',
        password: 'rootechs1234',
        name: 'Rootechs',
        role: 'ADMIN',
        schemaName: 'seoro',
        companyName: '(주)서로',
        permissions: ['*']
    }
];

// 토큰 가져오기 헬퍼 함수
const getToken = () => {
    return localStorage.getItem('token') || sessionStorage.getItem('token');
};

/**
 * 로그인 (데모용 - 백엔드 호출 없음)
 */
export const login = async (email, password, rememberMe = false) => {
    const matched = DEMO_USERS.find(u => u.email === email && u.password === password);
    if (matched) {
        const { password: _pw, ...user } = matched;
        const mockToken = 'demo-token-' + Date.now();

        // rememberMe에 따라 저장소 선택
        const storage = rememberMe ? localStorage : sessionStorage;
        storage.setItem('token', mockToken);
        storage.setItem('user', JSON.stringify(user));
        storage.setItem('rememberMe', rememberMe.toString());

        return {
            token: mockToken,
            user
        };
    }

    // 등록된 데모 계정이 아닌 경우 에러
    throw new Error('관리자에게 문의해주세요.\n관리자 이메일: skm@seoro.ai.kr');
};

/**
 * 로그아웃
 */
export const logout = async () => {
    // 두 스토리지 모두 정리
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('rememberMe');
    sessionStorage.removeItem('token');
    sessionStorage.removeItem('user');
    sessionStorage.removeItem('rememberMe');
};

/**
 * 현재 사용자 정보 조회
 */
export const getCurrentUser = async () => {
    const user = getStoredUser();
    if (user) {
        return user;
    }
    throw new Error('Not authenticated');
};

/**
 * 토큰 갱신 (데모용 - 항상 성공)
 */
export const refreshToken = async () => {
    const newToken = 'demo-token-' + Date.now();
    const rememberMe = localStorage.getItem('rememberMe') === 'true';
    const storage = rememberMe ? localStorage : sessionStorage;
    storage.setItem('token', newToken);
    return newToken;
};

/**
 * 로그인 상태 확인
 */
export const isAuthenticated = () => {
    const token = getToken();
    return !!token;
};

/**
 * 저장된 사용자 정보 가져오기
 */
export const getStoredUser = () => {
    const userStr = localStorage.getItem('user') || sessionStorage.getItem('user');
    if (userStr) {
        try {
            return JSON.parse(userStr);
        } catch (e) {
            console.error('Failed to parse user data:', e);
            return null;
        }
    }
    return null;
};

/**
 * 사용자 역할 확인
 */
export const isAdmin = () => {
    const user = getStoredUser();
    return user && user.role === 'ADMIN';
};

export const isOperator = () => {
    const user = getStoredUser();
    return user && user.role === 'OPERATOR';
};

export const isSuperAdmin = () => {
    const user = getStoredUser();
    return user && user.role === 'SUPER_ADMIN';
};

/**
 * 권한 확인 (데모용 - 항상 true)
 */
export const hasPermission = (permissionCode) => {
    const user = getStoredUser();
    if (!user) return false;

    // 데모에서는 모든 권한 허용
    if (user.permissions && user.permissions.includes('*')) return true;

    return true;
};

/**
 * 여러 권한 중 하나라도 있는지 확인
 */
export const hasAnyPermission = (permissionCodes) => {
    if (!permissionCodes || permissionCodes.length === 0) return true;
    return permissionCodes.some(perm => hasPermission(perm));
};

/**
 * 모든 권한이 있는지 확인
 */
export const hasAllPermissions = (permissionCodes) => {
    if (!permissionCodes || permissionCodes.length === 0) return true;
    return permissionCodes.every(perm => hasPermission(perm));
};

/**
 * 사용자 권한 목록 가져오기
 */
export const getUserPermissions = () => {
    const user = getStoredUser();
    return user?.permissions || [];
};

export default {
    login,
    logout,
    getCurrentUser,
    refreshToken,
    isAuthenticated,
    getStoredUser,
    isAdmin,
    isOperator,
    isSuperAdmin,
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    getUserPermissions
};
